import metaLogo from "assets/images/meta-logo.png";
import { ReactComponent as IconPulse } from "assets/svg/pulse.svg";
import { ReactComponent as TiktokIcon } from "assets/svg/tiktok-complex.svg";
import { Actions, ActionsContainer } from "Components/Modal";
import { useMobileMediaQuery } from "hooks";
import { Button } from "melodies-source/Button";
import { StackedModal } from "melodies-source/StackedModal";
import { useBoolean } from "melodies-source/utils";
import { useMemo } from "react";
import { sampleCode } from "Routes/SetPage/Sections/TrackingAndAnalytics";
import {
  Platform,
  PlatformCard,
} from "Routes/SetPage/Sections/TrackingAndAnalytics/PlatformCard";
import styled from "styled-components";
import {
  typedFields,
  useBuilderContext,
  useConfigSlice,
} from "../Components/BuilderContext";
import { PublishWarning } from "../Components/PublishWarning";

const FIELDS = typedFields(["metaPixel", "tiktokPixel"]);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

interface ThankYouMessageAddonProps {
  active?: boolean;
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
}

export const ThankYouMessageAddon = ({
  active,
  hideOnDesktop,
  hideOnMobile,
}: ThankYouMessageAddonProps) => {
  const { fields, setField } = useConfigSlice(FIELDS);
  const { status, hasUnpublishedField } = useBuilderContext();
  const [open, toggle] = useBoolean();
  const isMobile = useMobileMediaQuery();
  const isDraft = status === "draft";
  const hasPixel = FIELDS.some((field) => !!fields[field]?.content);
  const hasUnpublishedPixel = FIELDS.some(hasUnpublishedField);
  const showPublishWarning = !isDraft && hasPixel && hasUnpublishedPixel;

  const meta: Platform = useMemo(
    () => ({
      id: "metaTracking",
      logo: <img src={metaLogo} alt="Meta logo" width={60} />,
      name: "Meta Tracking Pixel",
      description:
        "Connect your Meta account to run pixel conversion tracking.",
      extractId: (embed) => {
        const group = embed.match(/fbq\('init',\s*'([A-Za-z0-9]+)'\)/);
        return group?.[1];
      },
      inputLabel: {
        id: "Meta Pixel ID",
        code: "Meta Dataset Code",
      },
      placeholder: {
        id: "Paste your ID here (example: A1AAA1AA1AAA1AAAAA1AA)",
        code: sampleCode.meta,
      },
      value: fields.metaPixel?.content ?? "",
      setValue: (value) => setField("metaPixel", value),
    }),
    [fields.metaPixel?.content, setField],
  );

  const tiktok: Platform = useMemo(
    () => ({
      id: "tiktokTracking",
      logo: <TiktokIcon />,
      name: "TikTok Tracking Pixel",
      description:
        "Add your TikTok Pixel code here to create an audience to remarket to.",
      extractId: (embed) => {
        const group = embed.match(/ttq.load\('([A-Za-z0-9]+)'\)/);
        return group?.[1];
      },
      inputLabel: {
        id: "TikTok Pixel ID",
        code: "TikTok Pixel Code",
      },
      placeholder: {
        id: "Paste your ID here (example: 1234567890)",
        code: sampleCode.tiktok,
      },
      value: fields.tiktokPixel?.content ?? "",
      setValue: (value) => setField("tiktokPixel", value),
    }),
    [fields.tiktokPixel?.content, setField],
  );

  if (!active || (hideOnDesktop && !isMobile) || (hideOnMobile && isMobile)) {
    return null;
  }

  return (
    <>
      <StackedModal
        header="Conversion Tracking"
        variant="medium"
        isOpen={open}
        onClose={toggle}
      >
        <Container>
          <PlatformCard platform={meta} />
          <PlatformCard platform={tiktok} />
          {showPublishWarning && (
            <PublishWarning message="Your pixel goes live when you publish the campaign." />
          )}
        </Container>
        <ActionsContainer>
          <Actions>
            <Button variant="outlined" onClick={toggle}>
              Done
            </Button>
          </Actions>
        </ActionsContainer>
      </StackedModal>
      <Button
        text
        type="button"
        leftIcon={<IconPulse />}
        onClick={toggle}
        style={{ padding: 0, height: "auto" }}
      >
        Conversion Tracking
      </Button>
    </>
  );
};
